import type { ComponentType, SVGProps } from 'react';
import {
  BookOpen,
  Box,
  CircleHelp,
  Key,
  MessageCircleMore,
  Package2,
  Settings,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { CourierPickupIcon } from '@/components/icons/courier-pickup-icon';
import { ExcludedProductsIcon } from '@/components/icons/excluded-products-icon';
import { NavUser } from '@/components/layout/nav-user';
import { WidgetsIcon } from '@/components/icons/widgets-icon';
import { InPostLogo, InPostMark } from '@/components/inpost-logo';
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';

// TODO: point this at the real feedback form once one exists.
const FEEDBACK_URL = '#todo';
const DOCUMENTATION_URL = '#todo';

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

function NavLinkItem({
  icon: Icon,
  label,
  to,
  active,
}: {
  icon: NavIcon;
  label: string;
  to: string;
  active: boolean;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={label}>
        <NavLink to={to} end>
          <Icon />
          <span>{label}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavActionItem({
  icon: Icon,
  label,
  onClick,
}: {
  icon: NavIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton tooltip={label} onClick={onClick}>
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebarContent() {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();

  return (
    <>
      <SidebarHeader>
        <div className="flex h-12 items-center justify-center">
          <InPostLogo
            variant="black-bg"
            className="h-10 w-auto group-data-[collapsible=icon]:hidden"
          />
          <InPostMark
            variant="black-bg"
            className="hidden h-7 w-auto group-data-[collapsible=icon]:block"
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.groups.logistics')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavLinkItem
                icon={Box}
                label={t('nav.shipments')}
                to="/shipments"
                active={pathname === '/shipments'}
              />
              <NavLinkItem
                icon={CourierPickupIcon}
                label={t('nav.pickupOrders')}
                to="/pickup-orders"
                active={pathname === '/pickup-orders'}
              />
              <NavLinkItem
                icon={Package2}
                label={t('nav.services')}
                to="/markets"
                active={pathname === '/markets'}
              />
              <NavLinkItem
                icon={ExcludedProductsIcon}
                label={t('nav.excludedProducts')}
                to="/excluded-products"
                active={pathname === '/excluded-products'}
              />
              <NavLinkItem
                icon={WidgetsIcon}
                label={t('nav.widgets')}
                to="/widgets"
                active={pathname === '/widgets'}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.groups.management')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavLinkItem
                icon={Key}
                label={t('nav.authorization')}
                to="/authorization"
                active={pathname === '/authorization'}
              />
              <NavLinkItem
                icon={Settings}
                label={t('nav.settings')}
                to="/settings"
                active={pathname === '/settings'}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.groups.support')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavLinkItem
                icon={CircleHelp}
                label={t('nav.help')}
                to="/help"
                active={pathname === '/help'}
              />
              <NavActionItem
                icon={BookOpen}
                label={t('nav.documentation')}
                onClick={() => window.open(DOCUMENTATION_URL, '_blank')}
              />
              <NavActionItem
                icon={MessageCircleMore}
                label={t('nav.feedback')}
                onClick={() => window.open(FEEDBACK_URL, '_blank')}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </>
  );
}
